import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import CandidateSearchFilters from '../components/Skelbimai/CandidateSearchFilters';
import CandidateGrid from '../components/Skelbimai/CandidateGrid';
import AddCandidateToSelectionModal from '../components/Employer/AddCandidateToSelectionModal';
import SelectAllToggle from '../components/Common/SelectAllToggle';
import { Grid, List, UserPlus, ChevronRight, X, Sparkles } from 'lucide-react';
import './Skelbimai.scss';

// Persistent (per-tab) marked-candidate set so employers can flip across pages
// and into candidate detail views without losing their selection.
const MARKED_STORAGE_KEY = 'gidas:skelbimai:marked_candidates';

type ViewMode = 'all' | 'hide-in-applications' | 'marked';

interface Candidate {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  profile_image_url?: string;
  work_experience: any[];
  education: any[];
  language_skills: any[];
  job_preferences: any;
  cv_visibility_restriction: boolean;
  created_at: string;
  updated_at: string;
}

// Loads marked-candidate map from sessionStorage. Defensive: any malformed
// payload or storage-disabled environment yields an empty Map.
const loadMarkedFromStorage = (): Map<number, Candidate> => {
  try {
    const raw = sessionStorage.getItem(MARKED_STORAGE_KEY);
    if (!raw) return new Map();
    const entries = JSON.parse(raw);
    if (!Array.isArray(entries)) return new Map();
    return new Map(entries as Array<[number, Candidate]>);
  } catch {
    return new Map();
  }
};

const persistMarkedToStorage = (map: Map<number, Candidate>) => {
  try {
    if (map.size === 0) {
      sessionStorage.removeItem(MARKED_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(MARKED_STORAGE_KEY, JSON.stringify(Array.from(map.entries())));
  } catch {
    // Quota exceeded or storage disabled — silently drop, marks remain in memory.
  }
};

export interface FilterOptions {
  searchKeyword: string;
  keywords: string[]; // Keyword chips
  location: string[];
  experience: string[];
  education: string[];
  languages: string[];
  computerSkills: string[];
  drivingLicense: string[];
  salary: string;
  workType: string[];
  age: string;
  gender: string[];
  status: string[];
  activity: string;
  cvLanguage: string[];
  hasPhoto: boolean;
  hasRecommendation: boolean;
  // Company-related filters
  companyNames: string[];
  lastWorkField: string[];
  companyActivity: string[];
  // Desired-job filters (from candidate CV's "Pageidaujamas darbas" section)
  desiredWorkField: string[];
  desiredPositionType: string[];
  desiredWorkTime: string[];
  desiredPosition: string[];
  desiredLocation: string[];
  // Freelance opt-in (CV has "I accept freelance gigs" checked)
  acceptsGigs: boolean;
  // Per-position minimum years of experience.
  // Key  = position name (must also be in `experience` to act as a filter)
  // Value = minimum total years across all stints in that position
  // Empty / 0 = no constraint, just the existence checkbox.
  positionExperienceYears: Record<string, number>;
  // Taxonomy-backed "Darbo patirtis" filters (ids). exp* = any experience,
  // last* = most-recent job. Sent to the API as comma-separated id params.
  expFieldIds: number[];
  expTipasIds: number[];
  expPositionIds: number[];
  lastFieldIds: number[];
  lastTipasIds: number[];
  lastPositionIds: number[];
}

// Convert frontend filters to backend query params
export const filtersToQueryParams = (filters: FilterOptions): URLSearchParams => {
  const params = new URLSearchParams();
  
  // Keywords
  if (filters.searchKeyword) {
    params.set('query', filters.searchKeyword);
  }
  if (filters.keywords.length > 0) {
    params.set('keywords', filters.keywords.join(','));
  }
  
  // Location
  if (filters.location.length > 0) {
    params.set('location', filters.location.join(','));
  }
  
  // Age (parse from "16-65" format)
  if (filters.age && filters.age !== '16-65') {
    const [min, max] = filters.age.split('-').map(Number);
    if (min > 16) params.set('ageMin', min.toString());
    if (max < 65) params.set('ageMax', max.toString());
  }
  
  // Gender
  if (filters.gender.length > 0) {
    params.set('gender', filters.gender.join(','));
  }
  
  // CV Language
  if (filters.cvLanguage.length > 0) {
    params.set('cvLanguage', filters.cvLanguage.join(','));
  }
  
  // Boolean filters
  if (filters.hasPhoto) {
    params.set('hasPhoto', 'true');
  }
  if (filters.hasRecommendation) {
    params.set('hasRecommendation', 'true');
  }
  
  // Activity
  if (filters.activity) {
    params.set('activity', filters.activity);
  }
  
  // Status
  if (filters.status.length > 0) {
    params.set('status', filters.status.join(','));
  }
  
  // Education
  if (filters.education.length > 0) {
    params.set('education', filters.education.join(','));
  }
  
  // Languages
  if (filters.languages.length > 0) {
    params.set('languages', filters.languages.join(','));
  }
  
  // Computer skills
  if (filters.computerSkills.length > 0) {
    params.set('computerSkills', filters.computerSkills.join(','));
  }
  
  // Driving license
  if (filters.drivingLicense.length > 0) {
    params.set('drivingLicense', filters.drivingLicense.join(','));
  }
  
  // Work type
  if (filters.workType.length > 0) {
    params.set('workType', filters.workType.join(','));
  }
  
  // Salary
  if (filters.salary) {
    params.set('salaryMin', filters.salary);
  }
  
  // Taxonomy-backed experience filters (ids). Each non-empty list is sent as a
  // comma-separated param consumed by the candidate search/count endpoints.
  const setIds = (key: string, ids?: number[]) => { if (ids && ids.length > 0) params.set(key, ids.join(',')); };
  setIds('expFieldIds', filters.expFieldIds);
  setIds('expTipasIds', filters.expTipasIds);
  setIds('expPositionIds', filters.expPositionIds);
  setIds('lastFieldIds', filters.lastFieldIds);
  setIds('lastTipasIds', filters.lastTipasIds);
  setIds('lastPositionIds', filters.lastPositionIds);

  // Legacy string work-field experience (kept for round-trip symmetry with
  // getInitialFilters, which reads `workFields`).
  if (filters.experience?.length > 0) {
    params.set('workFields', filters.experience.join(','));
  }

  // Company names + last-employer work field / activity
  if (filters.companyNames?.length > 0) {
    params.set('companyNames', filters.companyNames.join(','));
  }
  if (filters.lastWorkField?.length > 0) {
    params.set('lastWorkField', filters.lastWorkField.join(','));
  }
  if (filters.companyActivity?.length > 0) {
    params.set('companyActivity', filters.companyActivity.join(','));
  }

  // Desired job preference filters (from the candidate CV's "Pageidaujamas
  // darbas" section). All must round-trip so saved searches restore fully.
  if (filters.desiredWorkField?.length > 0) {
    params.set('desiredWorkField', filters.desiredWorkField.join(','));
  }
  if (filters.desiredPositionType?.length > 0) {
    params.set('desiredPositionType', filters.desiredPositionType.join(','));
  }
  if (filters.desiredPosition?.length > 0) {
    params.set('desiredPosition', filters.desiredPosition.join(','));
  }
  if (filters.desiredWorkTime?.length > 0) {
    params.set('desiredWorkTime', filters.desiredWorkTime.join(','));
  }
  if (filters.desiredLocation?.length > 0) {
    params.set('desiredLocation', filters.desiredLocation.join(','));
  }

  // Freelance opt-in
  if (filters.acceptsGigs) {
    params.set('acceptsGigs', 'true');
  }

  // Per-position minimum-years map (e.g. {"Transporto vadybininkas": 3}).
  // Strip out 0 / NaN entries so we never serialize a "no-op" constraint.
  if (filters.positionExperienceYears) {
    const cleaned: Record<string, number> = {};
    for (const [position, years] of Object.entries(filters.positionExperienceYears)) {
      const n = Number(years);
      if (Number.isFinite(n) && n > 0 && position.trim()) {
        cleaned[position] = n;
      }
    }
    if (Object.keys(cleaned).length > 0) {
      params.set('positionExperienceYears', JSON.stringify(cleaned));
    }
  }

  return params;
};

const Skelbimai: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [gridLayout, setGridLayout] = useState<'grid-3' | 'list'>('grid-3');
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 768);
  const [filtersClosing, setFiltersClosing] = useState(false);

  const handleCollapseFilters = useCallback(() => {
    setFiltersClosing(true);
    setTimeout(() => {
      setShowFilters(false);
      setFiltersClosing(false);
    }, 250);
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const viewMode: ViewMode = (() => {
    const v = searchParams.get('view');
    if (v === 'marked' || v === 'hide-in-applications') return v;
    return 'all';
  })();
  const setViewMode = (next: ViewMode) => {
    setSearchParams(prev => {
      if (next === 'all') prev.delete('view');
      else prev.set('view', next);
      return prev;
    }, { replace: true });
  };
  const [markedCandidates, setMarkedCandidates] = useState<Map<number, Candidate>>(() => loadMarkedFromStorage());
  const [aiBannerVisible, setAiBannerVisible] = useState(() => searchParams.get('ai') === '1');
  const [showJobPicker, setShowJobPicker] = useState(false);
  const candidatesPerPage = 20;
  const hideInApplications = viewMode === 'hide-in-applications';
  const markedIds = useMemo(() => new Set(markedCandidates.keys()), [markedCandidates]);
  const markedCount = markedCandidates.size;
  const countDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Mirror marked-candidate state into sessionStorage so navigating into a CV
  // detail page (and back) doesn't drop the employer's running selection.
  useEffect(() => {
    persistMarkedToStorage(markedCandidates);
  }, [markedCandidates]);

  // If the marked set empties out (e.g. after "Įkelti į atrankas" success),
  // there's nothing to show in marked view — fall back to "Rodyti visus".
  useEffect(() => {
    if (viewMode === 'marked' && markedCount === 0) {
      setViewMode('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, markedCount]);

  // Non-authenticated users see limited candidates
  const isGuest = !isAuthenticated;
  const guestCandidateLimit = 6;

  // Get initial filters from URL parameters (URL is the source of truth for filter state)
  const getInitialFilters = (): FilterOptions => {
    const baseFilters: FilterOptions = {
      searchKeyword: '',
      keywords: [],
      location: [],
      experience: [],
      education: [],
      languages: [],
      computerSkills: [],
      drivingLicense: [],
      salary: '',
      workType: [],
      age: '',
      gender: [],
      status: [],
      activity: '',
      cvLanguage: [],
      hasPhoto: false,
      hasRecommendation: false,
      companyNames: [],
      lastWorkField: [],
      companyActivity: [],
      desiredWorkField: [],
      desiredPositionType: [],
      desiredWorkTime: [],
      desiredPosition: [],
      desiredLocation: [],
      acceptsGigs: false,
      positionExperienceYears: {},
      expFieldIds: [],
      expTipasIds: [],
      expPositionIds: [],
      lastFieldIds: [],
      lastTipasIds: [],
      lastPositionIds: [],
    };

    // Taxonomy-backed experience id filters from the URL.
    const idsFrom = (key: string): number[] => {
      const raw = searchParams.get(key);
      if (!raw) return [];
      return raw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n > 0);
    };
    baseFilters.expFieldIds = idsFrom('expFieldIds');
    baseFilters.expTipasIds = idsFrom('expTipasIds');
    baseFilters.expPositionIds = idsFrom('expPositionIds');
    baseFilters.lastFieldIds = idsFrom('lastFieldIds');
    baseFilters.lastTipasIds = idsFrom('lastTipasIds');
    baseFilters.lastPositionIds = idsFrom('lastPositionIds');
    
    // Read ALL filters from URL params (URL is the source of truth)
    const queryParam = searchParams.get('query');
    const keywordsParam = searchParams.get('keywords');
    const locationParam = searchParams.get('location');
    const educationParam = searchParams.get('education');
    const languagesParam = searchParams.get('languages');
    const computerSkillsParam = searchParams.get('computerSkills');
    const drivingLicenseParam = searchParams.get('drivingLicense');
    const workTypeParam = searchParams.get('workType');
    const workFieldsParam = searchParams.get('workFields');
    const genderParam = searchParams.get('gender');
    const cvLanguageParam = searchParams.get('cvLanguage');
    const ageMinParam = searchParams.get('ageMin');
    const ageMaxParam = searchParams.get('ageMax');
    const activityParam = searchParams.get('activity');
    const statusParam = searchParams.get('status');
    const salaryMinParam = searchParams.get('salaryMin');
    const hasPhotoParam = searchParams.get('hasPhoto');
    const hasRecommendationParam = searchParams.get('hasRecommendation');
    const companyNamesParam = searchParams.get('companyNames');
    const lastWorkFieldParam = searchParams.get('lastWorkField');
    const companyActivityParam = searchParams.get('companyActivity');
    const desiredPositionParam = searchParams.get('desiredPosition');
    const desiredLocationParam = searchParams.get('desiredLocation');
    const acceptsGigsParam = searchParams.get('acceptsGigs');
    const positionExperienceYearsParam = searchParams.get('positionExperienceYears');
    // "positions" is set by the employer home-page search box and the EmployerDetailedSearch
    // component, but was never read here — causing the filter to be silently dropped and
    // all 9114 CVs to be returned regardless of what the employer typed.
    const positionsParam = searchParams.get('positions');
    
    if (queryParam) baseFilters.searchKeyword = queryParam;
    if (keywordsParam) baseFilters.keywords = keywordsParam.split(',').map(k => k.trim()).filter(k => k);
    // Merge position chips into keywords so each phrase becomes a keyword filter term.
    // A phrase like "Darbo saugos specialistas" is searched as a single LIKE pattern
    // across job_preferences, work_experience, and other CV text fields.
    if (positionsParam) {
      const positionTerms = positionsParam.split(',').map(p => p.trim()).filter(p => p);
      baseFilters.keywords = [...baseFilters.keywords, ...positionTerms];
    }
    if (locationParam) baseFilters.location = locationParam.split(',').map(l => decodeURIComponent(l));
    if (educationParam) baseFilters.education = educationParam.split(',');
    if (languagesParam) baseFilters.languages = languagesParam.split(',');
    if (computerSkillsParam) baseFilters.computerSkills = computerSkillsParam.split(',');
    if (drivingLicenseParam) baseFilters.drivingLicense = drivingLicenseParam.split(',');
    if (workTypeParam) baseFilters.workType = workTypeParam.split(',');
    if (workFieldsParam) baseFilters.experience = workFieldsParam.split(',');
    if (genderParam) baseFilters.gender = genderParam.split(',');
    if (cvLanguageParam) baseFilters.cvLanguage = cvLanguageParam.split(',');
    if (activityParam) baseFilters.activity = activityParam;
    if (statusParam) baseFilters.status = statusParam.split(',');
    if (salaryMinParam) baseFilters.salary = salaryMinParam;
    if (hasPhotoParam === 'true') baseFilters.hasPhoto = true;
    if (hasRecommendationParam === 'true') baseFilters.hasRecommendation = true;
    if (companyNamesParam) baseFilters.companyNames = companyNamesParam.split(',');
    if (lastWorkFieldParam) baseFilters.lastWorkField = lastWorkFieldParam.split(',');
    if (companyActivityParam) baseFilters.companyActivity = companyActivityParam.split(',');
    const desiredWorkFieldParam = searchParams.get('desiredWorkField');
    const desiredPositionTypeParam = searchParams.get('desiredPositionType');
    const desiredWorkTimeParam = searchParams.get('desiredWorkTime');
    if (desiredWorkFieldParam) baseFilters.desiredWorkField = desiredWorkFieldParam.split(',').map(f => decodeURIComponent(f));
    if (desiredPositionTypeParam) baseFilters.desiredPositionType = desiredPositionTypeParam.split(',').map(t => decodeURIComponent(t));
    if (desiredWorkTimeParam) baseFilters.desiredWorkTime = desiredWorkTimeParam.split(',').map(t => decodeURIComponent(t));
    if (desiredPositionParam) baseFilters.desiredPosition = desiredPositionParam.split(',').map(p => decodeURIComponent(p));
    if (desiredLocationParam) baseFilters.desiredLocation = desiredLocationParam.split(',').map(l => decodeURIComponent(l));
    if (acceptsGigsParam === 'true') baseFilters.acceptsGigs = true;
    if (positionExperienceYearsParam) {
      try {
        const parsed = JSON.parse(positionExperienceYearsParam);
        if (parsed && typeof parsed === 'object') {
          const safe: Record<string, number> = {};
          for (const [k, v] of Object.entries(parsed)) {
            const n = Number(v);
            if (Number.isFinite(n) && n > 0) safe[k] = n;
          }
          baseFilters.positionExperienceYears = safe;
        }
      } catch {
        // Bad JSON in the URL — ignore and keep defaults.
      }
    }
    
    // Build age string from min/max
    if (ageMinParam || ageMaxParam) {
      const minAge = ageMinParam || '16';
      const maxAge = ageMaxParam || '65';
      baseFilters.age = `${minAge}-${maxAge}`;
    }
    
    return baseFilters;
  };

  const [filters, setFilters] = useState<FilterOptions>(getInitialFilters());

  // Fetch live count from backend (debounced)
  const fetchLiveCount = useCallback(async (filterOptions: FilterOptions) => {
    if (countDebounceRef.current) {
      clearTimeout(countDebounceRef.current);
    }

    countDebounceRef.current = setTimeout(async () => {
      try {
        setCountLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const token = localStorage.getItem('auth_token');
        
        const queryParams = filtersToQueryParams(filterOptions);
        if (hideInApplications && !isGuest) {
          queryParams.set('hideInApplications', 'true');
        }
        
        const endpoint = isGuest 
          ? `${API_BASE_URL}/cv/public/count`
          : `${API_BASE_URL}/cv/employer/count`;
        
        const url = `${endpoint}?${queryParams.toString()}`;
        
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        
        if (!isGuest && token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(url, { headers });
        
        if (response.ok) {
          const result = await response.json();
          setLiveCount(result.count);
        }
      } catch (error) {
        console.error('Error fetching count:', error);
      } finally {
        setCountLoading(false);
      }
    }, 300);
  }, [isGuest, hideInApplications]);

  // Fetch candidates from backend with ALL filters
  const fetchCandidates = useCallback(async (filterOptions: FilterOptions, page: number = 1, hideInApp?: boolean) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const queryParams = filtersToQueryParams(filterOptions);
      queryParams.set('page', page.toString());
      queryParams.set('limit', candidatesPerPage.toString());
      
      const shouldHide = hideInApp ?? hideInApplications;
      if (shouldHide && !isGuest) {
        queryParams.set('hideInApplications', 'true');
      }
      
      const endpoint = isGuest
        ? `${API_BASE_URL}/cv/public/preview`
        : `${API_BASE_URL}/cv/employer/search`;
      
      const url = `${endpoint}?${queryParams.toString()}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (!isGuest && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(url, { headers });

      if (response.ok) {
        const result = await response.json();
        const allCandidates = result.data || [];
        const total = result.pagination?.total || result.total || allCandidates.length;
        
        setCandidates(allCandidates);
        setTotalResults(total);
        setLiveCount(total);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch candidates:', errorData.message);
        setCandidates([]);
        setTotalResults(0);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setCandidates([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [isGuest, hideInApplications]);

  // Apply filters - fetch from backend with all filters
  const applyFilters = useCallback((newFilters: FilterOptions) => {
    console.log('🔍 Applying filters:', newFilters);
    
    // Update URL with all filters (page reset to 1 by omitting page param)
    const newParams = filtersToQueryParams(newFilters);
    // Preserve mode param if present
    const mode = searchParams.get('mode');
    if (mode) newParams.set('mode', mode);
    
    setFilters(newFilters);
    setSearchParams(newParams, { replace: true });
    // useEffect on searchParams change handles the fetch
  }, [searchParams, setSearchParams]);

  // Handle filter preview (just update count without fetching full results)
  const previewFilters = useCallback((newFilters: FilterOptions) => {
    fetchLiveCount(newFilters);
  }, [fetchLiveCount]);

  const getCurrentPageCandidates = () => {
    // In "marked" view, render the full marked set (typically <100 entries)
    // without server pagination — the user already curated this list manually.
    if (viewMode === 'marked') {
      return Array.from(markedCandidates.values());
    }

    // For guests, limit to 6 candidates total
    if (isGuest) {
      return candidates.slice(0, guestCandidateLimit);
    }

    // Backend already returns paginated results
    return candidates;
  };

  const handlePageChange = (page: number) => {
    // Marked selection now persists across pagination, so we deliberately
    // do NOT reset markedCandidates here.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Persist page in URL — triggers useEffect which fetches the right page
    // and ensures returnUrl captures the page for back navigation
    const newParams = new URLSearchParams(searchParams.toString());
    if (page === 1) {
      newParams.delete('page');
    } else {
      newParams.set('page', page.toString());
    }
    setSearchParams(newParams, { replace: true });
  };

  const totalPages = viewMode === 'marked'
    ? 1
    : Math.ceil(totalResults / candidatesPerPage);

  // Initial fetch with URL parameters — wait for auth to resolve before fetching
  // so we always use the correct endpoint (employer vs public)
  useEffect(() => {
    if (isLoading) return; // Auth still resolving; effect will re-run when isLoading → false
    const initialFilters = getInitialFilters();
    setFilters(initialFilters);
    
    // Read page from URL (restored when navigating back from candidate detail)
    const pageFromUrl = parseInt(searchParams.get('page') || '1');
    setCurrentPage(pageFromUrl);
    fetchCandidates(initialFilters, pageFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoading, fetchCandidates]); // Re-fetch when URL params change or auth resolves

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="skelbimai-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  if (isAuthenticated && user?.user_type !== 'employer') {
    return <Navigate to="/darbo-skelbimai" replace />;
  }

  if (loading) {
    return (
      <div className="skelbimai-page">
        <div className="skelbimai-loading">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Kraunami kandidatai...</p>
          </div>
        </div>
      </div>
    );
  }

  const aiQuery = searchParams.get('aiQuery') || '';
  const aiFilterChips: { label: string; value: string }[] = [];
  if (searchParams.get('location')) aiFilterChips.push({ label: 'Miestas', value: searchParams.get('location')! });
  if (searchParams.get('workFields')) aiFilterChips.push({ label: 'Darbo sritis', value: searchParams.get('workFields')! });
  if (searchParams.get('salaryMin')) aiFilterChips.push({ label: 'Atlyginimas', value: `iki ${Number(searchParams.get('salaryMin')).toLocaleString('lt-LT')} €` });
  if (searchParams.get('positions')) aiFilterChips.push({ label: 'Pareigybė', value: searchParams.get('positions')! });

  return (
    <div className="skelbimai-page">
      {/* AI search banner */}
      {aiBannerVisible && (
        <div className="ai-search-banner">
          <div className="ai-search-banner__inner">
            <Sparkles size={16} className="ai-search-banner__icon" />
            <span className="ai-search-banner__label">AI paieška:</span>
            {aiQuery && <span className="ai-search-banner__query">&bdquo;{aiQuery}&ldquo;</span>}
            <div className="ai-search-banner__chips">
              {aiFilterChips.map(c => (
                <span key={c.label} className="ai-search-banner__chip">
                  <strong>{c.label}:</strong> {c.value}
                </span>
              ))}
            </div>
          </div>
          <button className="ai-search-banner__close" onClick={() => setAiBannerVisible(false)} title="Uždaryti">
            <X size={15} />
          </button>
        </div>
      )}
      {/* Main Content Area */}
      <div className="skelbimai-content">
        {/* Filters Sidebar */}
        {showFilters && (
          <div className={`filter-sidebar-employer${filtersClosing ? ' closing' : ''}`}>
            <div className="filter-sidebar-inner">
              <CandidateSearchFilters 
                isVisible={showFilters}
                onFiltersChange={applyFilters}
                onFiltersPreview={previewFilters}
                onCollapse={handleCollapseFilters}
                currentFilters={filters}
                liveCount={liveCount}
                countLoading={countLoading}
                isGuest={isGuest}
                hideSaveButton={isGuest}
              />
            </div>
          </div>
        )}
        
        {/* Main Content */}
        <div className="main-content">
          {/* Content Header with Title and Controls */}
          <div className="content-header-bar">
            <div className="header-left">
              {!showFilters && (
                <button
                  className="sidebar-reopen-btn"
                  onClick={() => setShowFilters(true)}
                  aria-label="Atidaryti filtrus"
                >
                  <ChevronRight size={16} />
                </button>
              )}
              <div>
                <h2>
                  {viewMode === 'marked'
                    ? `Pažymėti kandidatai (${markedCount})`
                    : `Kandidatų CV (${totalResults})`}
                </h2>
                <div className="breadcrumb">
                  <span>Titulinis</span>
                  <span>›</span>
                  <span>{viewMode === 'marked' ? 'Pažymėti' : 'Nauja paieška'}</span>
                </div>
              </div>
            </div>

            <div className="header-controls">
              {!isGuest && (() => {
                // "Žymėti visus" toggles only the *current page* of results so
                // that marks accumulated on previous pages are preserved. Page-only
                // semantics are owned here — the SelectAllToggle component is
                // purely visual and reflects the current page's filled-state via
                // selectedCount/totalCount. The dynamic label is what makes the
                // page-only behaviour discoverable to the user.
                const markedOnPage = candidates.filter(c => markedIds.has(c.id)).length;
                const pageCandidatesAllMarked =
                  candidates.length > 0 && markedOnPage === candidates.length;
                const inMarkedView = viewMode === 'marked';
                return (
                  <div className="control-group">
                    <SelectAllToggle
                      selectedCount={markedOnPage}
                      totalCount={candidates.length}
                      label={pageCandidatesAllMarked ? 'Atžymėti puslapį' : 'Žymėti visus'}
                      disabled={candidates.length === 0 || inMarkedView}
                      title={inMarkedView ? 'Grįžkite į pilną sąrašą, kad galėtumėte žymėti' : undefined}
                      onToggle={() => {
                        setMarkedCandidates(prev => {
                          const next = new Map(prev);
                          if (pageCandidatesAllMarked) {
                            candidates.forEach(c => next.delete(c.id));
                          } else {
                            candidates.forEach(c => next.set(c.id, c));
                          }
                          return next;
                        });
                      }}
                    />
                    <button
                      className="action-btn"
                      disabled={markedCount === 0}
                      onClick={() => setShowJobPicker(true)}
                    >
                      Įkelti į atrankas{markedCount > 0 ? ` (${markedCount})` : ''}
                    </button>
                    {markedCount > 0 && (
                      <button
                        className="action-btn action-btn--ghost"
                        onClick={() => setMarkedCandidates(new Map())}
                        title="Atšaukti visus pažymėjimus"
                      >
                        <X size={14} />
                        Atšaukti pažymėjimus
                      </button>
                    )}
                  </div>
                );
              })()}

              {!isGuest && (
                <div className="sort-controls">
                  <select
                    value={viewMode}
                    onChange={(e) => {
                      const next = e.target.value as ViewMode;
                      setViewMode(next);
                      // No backend roundtrip needed when switching into marked view —
                      // the candidate objects are already cached in markedCandidates.
                      if (next !== 'marked') {
                        setCurrentPage(1);
                        fetchCandidates(filters, 1, next === 'hide-in-applications');
                      }
                    }}
                  >
                    <option value="all">Rodyti visus</option>
                    <option value="hide-in-applications">Nerodyti įkeltų į atrankas</option>
                    <option value="marked" disabled={markedCount === 0}>
                      Rodyti pažymėtus{markedCount > 0 ? ` (${markedCount})` : ''}
                    </option>
                  </select>
                </div>
              )}

              <div className="view-controls">
                
                <button
                  className={`layout-btn ${gridLayout === 'grid-3' ? 'active' : ''}`}
                  onClick={() => setGridLayout('grid-3')}
                  title="Tinklelis"
                >
                  <Grid size={16} />
                </button>
                <button
                  className={`layout-btn ${gridLayout === 'list' ? 'active' : ''}`}
                  onClick={() => setGridLayout('list')}
                  title="Sąrašas"
                >
                  <List size={16} />
                </button>
                
              </div>
            </div>
          </div>

          <CandidateGrid
            candidates={getCurrentPageCandidates()}
            layout={gridLayout}
            loading={viewMode === 'marked' ? false : loading}
            isGuest={isGuest}
            selectedCandidates={markedIds}
            onToggleSelect={(id) => {
              setMarkedCandidates(prev => {
                const next = new Map(prev);
                if (next.has(id)) {
                  next.delete(id);
                  return next;
                }
                // Resolve the full Candidate from whichever source is currently
                // visible: backend results in normal view, the marked map in
                // marked view.
                const cand =
                  candidates.find(c => c.id === id) ||
                  Array.from(prev.values()).find(c => c.id === id);
                if (cand) next.set(id, cand);
                return next;
              });
            }}
          />

          {/* Guest Register CTA */}
          {isGuest && totalResults > guestCandidateLimit && (
            <div className="guest-register-cta">
              <div className="cta-content">
                <UserPlus size={32} />
                <div className="cta-text">
                  <h3>Norite matyti daugiau kandidatų?</h3>
                  <p>
                    Rasta <strong>{totalResults}</strong> kandidatų. 
                    Registruokitės ir gaukite prieigą prie visų kandidatų profilių.
                  </p>
                </div>
                <Link to="/register?type=employer" className="btn-register">
                  Registruotis
                </Link>
              </div>
            </div>
          )}
          
          {/* Pagination Footer - only for authenticated users (and not in marked view) */}
          {!isGuest && viewMode !== 'marked' && totalPages > 1 && (
            <div className="pagination">
              <span className="page-info">
                {currentPage} iš {totalPages}
              </span>

              <div className="page-controls">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  ‹
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {!isGuest && showJobPicker && (
        <AddCandidateToSelectionModal
          cvIds={Array.from(markedCandidates.keys())}
          candidateName={markedCount === 1 ? 'Pasirinktas kandidatas' : `${markedCount} kandidatai`}
          onClose={() => setShowJobPicker(false)}
          onSuccess={(added, skipped) => {
            setShowJobPicker(false);
            setMarkedCandidates(new Map());
            if (hideInApplications) {
              fetchCandidates(filters, currentPage);
            }
            showToast(`Pridėta: ${added}${skipped > 0 ? `, praleista (jau yra): ${skipped}` : ''}`, 'success');
          }}
        />
      )}
    </div>
  );
};

export default Skelbimai;
